import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import { baseURL } from "../../../config/api";
import useAuth from "../../../hooks/useAuth";
import useConfirm from "../../../hooks/useConfirm";
import useDelete from "../../../hooks/useDelete";
import { PhoneNumberInput } from "../../../components/PhoneNumberInput";
import PasswordValidation from "../../../components/PasswordValidation";
import EmailAvailabilityField from "../../../components/EmailAvailabilityField";
import UsernameAvailabilityField from "../../../components/UsernameAvailabilityField";
import { checkUsername } from "../../../services/CheckUsername";
import { checkEmail } from "../../../services/EmailCheck";

export const EditUser = () => {
    const navigate = useNavigate();
    const idNumber = useLocation();
    const { auth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [userName, setUserName] = useState<string>("");
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [mobilPhoneNumber, setMobilePhoneNumber] = useState<string>("");
    const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
    const [enabled, setEnabled] = useState<string>("Yes");
    const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy KK:mm a");
    const [roleId, setRoleId] = useState<string>("");
    const [roleOptions, setRoleOptions] = useState([]);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const [userData, setUserData] = useState<any>(null);

    useEffect(() => {
        axios.get(`${baseURL}roles/`)
            .then((response) => {
                const roles = response.data.data || [];

                setRoleOptions(
                    roles.map((item: any) => ({
                        value: String(item.id),
                        label: item.name,
                    }))
                );
            })
            .catch((error) => {
                console.error(error);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setPleaseWaitPopup(true);
        axios
            .get(`${baseURL}users/${idNumber?.state}`)
            .then((response) => {
                const user = response?.data?.data;
                setUserData(user);
                setFirstName(user?.firstName);
                setLastName(user?.lastName);
                setUserName(user?.username);
                setEmailAddress(user?.emailAddress);
                setDateFormat(user?.dateFormat);
                setMobilePhoneNumber(user?.mobilePhoneNumber ?? "");
                setEnabled(user?.isActive ? "Yes" : "No");
                setRoleId(String(user?.roleId));
                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);

        await axios
            .put(
                `${baseURL}users/${idNumber?.state}`,
                JSON.stringify({
                    ...userData,
                    firstName,
                    lastName,
                    userName,
                    password,
                    mobilPhoneNumber,
                    emailAddress,
                    profilePic: "",
                    isActive: enabled === "Yes" ? true : false,
                    roleId,
                    dateFormat,
                    updatedAt: new Date().toLocaleString(),
                    updatedBy: auth?.givenname

                }),
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: false,
                }
            )
            .then((response) => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                console.error("Error:", error);
            });
    }

    const deletingUser = async (id: number) => {
        setDeleteData({
            id: id,
            title: `Delete User`,
            summary: `Are you sure you want to delete this user?`,
            isDelete: (callback: any) => {
                setPleaseWaitPopup(true);
                callback();

                axios
                    .delete(`${baseURL}users?id=${id}&userId=${auth?.id}`)
                    .then(() => {
                        setPleaseWaitPopup(false);
                        navigate(-1);
                    })
                    .catch((error) => {
                        setConfirmData({
                            title: "Warning",
                            summary: error.response?.data || "Delete failed.",
                            confirmBtn: "Ok",
                            isConfirm: (cb: any) => cb(),
                        });

                        setConfirmPopup(true);
                        setPleaseWaitPopup(false);
                    });
            },
        });

        setDeletePopup(true);
    };

    return (
        <>
            <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
                <div className="w-full bg-white p-4 rounded-md">
                    <h2 className={`text-lg font-semibold`}>Edit User</h2>

                    <div className="mt-2 p-2">
                        <form onSubmit={(e) => handleSubmit(e)}>
                            <div className="w-full">
                                <div className="grid grid-cols-3 gap-6 my-2.5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={firstName}
                                            placeholder="First Name"
                                            onChange={(e) => setFirstName(e.target.value)}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Last Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary"
                                            value={lastName}
                                            placeholder="Last Name"
                                            onChange={(e) => setLastName(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-6 my-2.5">
                                    <div>
                                        <UsernameAvailabilityField
                                            username={userName}
                                            setUsername={setUserName}
                                            getUsernameChecking={checkUsername}
                                            label="Username"
                                            placeholder="Enter Username"
                                            required
                                        />
                                    </div>

                                    <PasswordValidation
                                        password={password}
                                        setPassword={setPassword}
                                        isValid={isValidPassword}
                                        setIsValid={setIsValidPassword}
                                        touched={passwordTouched}
                                        setTouched={setPasswordTouched}
                                        className={{
                                            parentDiv: "relative",
                                            label: "block text-sm font-medium text-gray-700 mb-2",
                                            input:
                                                "w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary",
                                        }}
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-6 my-2.5">
                                    <div>
                                        <EmailAvailabilityField
                                            emailAddress={emailAddress}
                                            setEmailAddress={setEmailAddress}
                                            getEmailChecking={checkEmail}
                                            label="Email Address"
                                            placeholder="Enter email address"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Mobile Number <span className="text-red-500">*</span>
                                        </label>
                                        <PhoneNumberInput
                                            name={"phoneNumber"}
                                            value={mobilPhoneNumber}
                                            setValue={setMobilePhoneNumber}
                                            fontSize={"14px"}
                                            required={true}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-6 my-2.5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                        <select
                                            className={`w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary`}
                                            value={roleId}
                                            onChange={(e) => setRoleId(e.target.value)}
                                        >
                                            {roleOptions?.map((role: any) => (
                                                <option key={role.value} value={String(role.value)}>
                                                    {role.label}
                                                </option>
                                            ))}

                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Active</label>
                                        <select
                                            className={`w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary`}
                                            value={enabled}
                                            onChange={(e) => setEnabled(e.target.value)}
                                        >
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-6 my-2.5">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                                        <select
                                            className={`w-full h-12 px-4 border border-gray-300 rounded-md bg-white outline-none focus:border-primary`}
                                            value={dateFormat}
                                            onChange={(e) => setDateFormat(e.target.value)}
                                        >
                                            <option value="MM/dd/yyyy KK:mm a">Use System Default</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-[#FFFFFF] text-secondary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deletingUser(userData?.id)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-red-500 text-white cursor-pointer border border-red-600"
                                    >
                                        Delete
                                    </button>

                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <PleasewaitPopup showPopup={pleaseWaitPopup} />
        </>
    )
}